import formatDate from "astrid-helpers/src/formatDate";

import articleDeliveryTypes from "../articles/constants/articleDeliveryTypes";
import articleTypes from "../articles/constants/articleTypes";
import contributorRoles from "../articles/constants/contributorRoles";
import getArticleChannelData from "../articles/getArticleChannelData";
import artifactValidationStatuses from "../artifacts/constants/artifactValidationStatuses";

import articleHasChannelDates from "./articleHasChannelDates";
import distributionStatuses from "./constants/distributionStatuses";

const requiredMetadataKeys = ["isbn", "name", "language", "synopsis", "narrator"];
const requiredMetadataKeysForTakedown = ["isbn", "name"];

function deliveryError(message, deliveryTypes) {
	const error = new Error(message);
	error.deliveryTypes = deliveryTypes;
	return error;
}

export default async function validateDistribution({ article, channel, delivery, ignoreDeliveryInProgress = false }) {
	const { metadata: orderedDeliveryOfMetadata, artifact: orderedDeliveryOfArtifact } = delivery;
	const { metadata = {}, artifact = {} } = article?.channels?.[channel.id]?.delivery || {};

	if (!orderedDeliveryOfMetadata && !orderedDeliveryOfArtifact) {
		throw deliveryError("At least one delivery type must be selected", [
			articleDeliveryTypes.METADATA,
			articleDeliveryTypes.ARTIFACT,
		]);
	}

	if (orderedDeliveryOfMetadata) {
		if (!ignoreDeliveryInProgress && deliveryInProgress(metadata)) {
			throw deliveryError("Delivery of metadata is already in progress", [articleDeliveryTypes.METADATA]);
		}

		if (!articleHasChannelDates(article, channel.id)) {
			throw deliveryError("At least one of announcement date, release date or takedown date must be set", [
				articleDeliveryTypes.METADATA,
			]);
		}

		const takedownDate = getArticleChannelData(article, channel.id, "takedownDate");
		const isTakedown = takedownDate && takedownDate <= formatDate();
		const requiredKeys = isTakedown
			? requiredMetadataKeysForTakedown
			: [...requiredMetadataKeys, ...(channel.requiredMetadataKeys || [])];

		const missingKeys = requiredKeys.filter((key) => isMissing({ article, channel, key }));

		if (missingKeys.length > 0) {
			throw deliveryError(`Required metadata fields are missing: ${missingKeys.join(", ")}`, [
				articleDeliveryTypes.METADATA,
			]);
		}
	}

	if (orderedDeliveryOfArtifact) {
		const theArtifact = article.artifact?.ref && (await article.artifact?.ref?.get());

		if (theArtifact) {
			const artifactStatus = theArtifact?.data?.()?.status;

			if ([artifactValidationStatuses.FAILED, artifactValidationStatuses.INVALID].includes(artifactStatus)) {
				throw deliveryError("The files are invalid and cannot be distributed.", [
					articleDeliveryTypes.ARTIFACT,
				]);
			}

			if (artifactStatus !== artifactValidationStatuses.VALID) {
				throw deliveryError(
					"The files are not ready for distribution. We are currently validating them. Please try again later.",
					[articleDeliveryTypes.ARTIFACT],
				);
			}

			if (!ignoreDeliveryInProgress && deliveryInProgress(artifact)) {
				throw deliveryError("Delivery of files is already in progress", [articleDeliveryTypes.ARTIFACT]);
			}

			if (!theArtifact?.markers?.length && theArtifact?.files?.length > 1) {
				throw deliveryError("Files are missing chapter markers", [articleDeliveryTypes.ARTIFACT]);
			}
		}
	}
}

function isMissing({ article, channel, key }) {
	switch (key) {
		case "narrator":
			return (
				article.type === articleTypes.AUDIOBOOK &&
				!article.contributors.some((contributor) => contributor.role === contributorRoles.NARRATOR)
			);

		default:
			const value = getArticleChannelData(article, channel.id, key);

			if (Array.isArray(value)) {
				return value.length === 0;
			}

			return !value;
	}
}

function deliveryInProgress({ distribution }) {
	return [
		distributionStatuses.QUEUED,
		distributionStatuses.PENDING,
		distributionStatuses.INITIALIZED,
		distributionStatuses.RUNNING,
	].includes(distribution?.status);
}
